import React, { useCallback, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import Carousel from 'nuka-carousel';
import { Link } from 'react-router-dom';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings';
import StorageHandler from 'config/StorageHandler';

import GlassCard from '../../GlassCard';
// import { GlassCard } from '../../GlassCard/GlassCard';

const typeToColor = {
  alert: '#FF6666',
  message: '#66CCFF',
  contest: '#66CC66',
  badge: '#CC66CC',
  'content-update': '#FFFF99',
  events: '#FF9966',
};

const typeToIcon = {
  alert: 'triangle-exclamation',
  message: 'hand-wave',
  contest: 'circle-dollar',
  badge: 'award',
  'content-update': 'memo-circle-check',
  events: 'calendar',
};

/** @param rawType */
const accentTopTypeClassSuffix = (rawType) => {
  if (rawType === 'default') return 'message';
  return rawType in typeToColor ? rawType : 'message';
};

const FANCYBOX_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'];

/** @param type */
const notificationTypeLabel = (type) => {
  const translated = i18n.translate(`notification.${type}`);
  const key = `notification.${type}`;
  if (!translated || translated === key) {
    return i18n.translate('notification.other') || 'Notification';
  }
  return translated;
};

/**
 * @param root0
 * @param root0.message
 * @param root0.weblink
 * @param root0.onTrackShare
 */
const NotificationSlideContent = ({ message, weblink, onTrackShare }) => {
  const filteredMessage = message.replace(/<p><br><\/p>/gi, '');
  const trimmedLink = weblink.trim();
  const fileExtension = trimmedLink.split('/').pop()?.split('.').pop()?.toLowerCase() ?? '';

  if (!trimmedLink) {
    return <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />;
  }

  if (FANCYBOX_EXTENSIONS.includes(fileExtension)) {
    return (
      <a
        className="notifications-card__weblink"
        data-fancybox
        data-type="iframe"
        data-src={trimmedLink}
        href={trimmedLink}
        onClick={() => {
          onTrackShare(trimmedLink);
        }}
      >
        <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
      </a>
    );
  }

  if (trimmedLink.includes('http')) {
    return (
      <a
        className="notifications-card__weblink"
        target="_blank"
        href={trimmedLink}
        rel="noreferrer"
        onClick={() => {
          onTrackShare(trimmedLink);
        }}
      >
        <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
      </a>
    );
  }

  return (
    <Link
      className="notifications-card__weblink"
      to={trimmedLink}
      onClick={() => {
        onTrackShare(trimmedLink);
      }}
    >
      <div className="notifications-card__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
    </Link>
  );
};

/**
 * @param root0
 * @param root0.appId
 * @param root0.notifications
 * @param root0.isLoading
 * @param root0.error
 * @param root0.span
 * @param root0.variant
 * @param root0.lightTheme
 */
const NotificationsCard = ({ appId, notifications, isLoading, error, span, variant: variantProp, lightTheme = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const onTrackShare = useCallback(
    (url) => {
      void StorageHandler.load(storageKeys.user).then(
        (raw) => {
          const userObj = raw;
          const email = userObj?.email ?? 'Guest';
          void APIHandler.trackEvent(appId, email, 'collateral', 'notification', url);
        },
        () => {
          void APIHandler.trackEvent(appId, 'Guest', 'collateral', 'notification', url);
        },
      );
    },
    [appId],
  );

  const currentType = notifications[currentIndex]?.type ?? 'default';
  const typeForLabel = currentType === 'default' ? 'other' : currentType;
  const currentColor = typeToColor[currentType] ?? typeToColor.message;
  const currentIcon = typeToIcon[currentType];
  const showCarousel = notifications.length > 0;

  const cardTitle = i18n.translate('notification.newAndNow') || 'New & Now!';
  const carouselAria = i18n.translate('notification.newAndNow') || 'New and announcements';
  const loadingMessage = i18n.translate('glassWidgets.notifications.loading') || 'Loading…';

  const effectiveVariant = variantProp ?? 'accent';

  const typeBadge = showCarousel ? (
    <span className="notifications-card__type-badge" style={{ color: currentColor }}>
      {currentIcon ? (
        <FontAwesomeIcon icon={['far', currentIcon]} className="notifications-card__type-badge-icon" />
      ) : null}
      <span className={clsx('notifications-card__type-badge-label', 'global__text--subtitle')}>
        {notificationTypeLabel(typeForLabel)}
      </span>
    </span>
  ) : undefined;

  return (
    <GlassCard
      title={cardTitle}
      span={span}
      variant={effectiveVariant}
      lightTheme={lightTheme}
      className={clsx(
        'notifications-card',
        effectiveVariant === 'accent' && `notifications-card--accent-top-${accentTopTypeClassSuffix(currentType)}`,
      )}
      headerExtra={typeBadge}
    >
      {isLoading && <div className="notifications-card__empty">{loadingMessage}</div>}

      {!isLoading && error && <div className="notifications-card__empty notifications-card__error">{error}</div>}

      {!isLoading && !error && showCarousel && (
        <div className="notifications-card__carousel" role="region" aria-label={carouselAria}>
          <Carousel
            autoplay={notifications.length > 1}
            autoplayInterval={15000}
            wrapAround
            pauseOnHover
            slideIndex={currentIndex}
            afterSlide={setCurrentIndex}
            withoutControls={notifications.length === 1}
            renderCenterLeftControls={() => null}
            renderCenterRightControls={() => null}
            defaultControlsConfig={{
              pagingDotsStyle: {
                fill: 'white',
              },
            }}
          >
            {notifications.map(({ id, message, weblink }) => (
              <div key={id} className="notifications-card__slide" aria-live="polite">
                <NotificationSlideContent message={message} weblink={weblink} onTrackShare={onTrackShare} />
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </GlassCard>
  );
};

export default NotificationsCard;
